import { type BaseErrorOptions, createErrorClass } from '@canvas/error';

export interface UserResponse {
  id: string;
  accountId: string;
  name: string;
  picture: string;
  locale: string;
  nickname?: string;
  email?: string;
}

interface GraphQLError {
  message: string;
}

interface GraphQLResponse {
  data?: {
    users?: UserResponse[];
  };
  errors?: GraphQLError[];
}

export class IdentityProfileFetchError extends createErrorClass(
  'IdentityProfileFetchError'
) {
  constructor(readonly errors: ReadonlyArray<GraphQLError>, options?: BaseErrorOptions) {
    super('Failed to fetch identity profiles', options);
  }
}

/**
 * Fetches full identity profiles for the given account IDs.
 * This function is built to be used within a DataLoader.
 *
 * @param accountIds The account ids to fetch
 * @returns An array of accounts (or undefined if not found) in the
 * same order as the input array
 */
export async function loadFullIdentityProfiles(
  accountIds: readonly string[]
): Promise<(UserResponse | undefined)[]> {
  const response = await fetch('/gateway/api/graphql', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      operationName: 'usersQuery',
      variables: { accountIds },
      query: `
          query usersQuery($accountIds: [ID!]!) {
            users(accountIds: $accountIds) {
              __typename
              id
              accountId
              name
              picture
              ... on AtlassianAccountUser {
                locale
                nickname
                email
              }
              ... on CustomerUser {
                locale
              }
            }
          }
        `,
    }),
  });

  const result = (await response.json()) as GraphQLResponse;

  if (result.errors) {
    throw new IdentityProfileFetchError(result.errors);
  }

  const users = result.data?.users ?? [];
  const usersById: Map<string, UserResponse> = new Map();

  for (const user of users) {
    usersById.set(user.accountId, user);
  }

  // User accounts must be returned in the same order as in the input list
  // as this is used within DataLoader in both batching and caching.
  // Values not in the input list should be undefined.
  return accountIds.map((id) => usersById.get(id));
}
